import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FileText, Package, CheckCircle, Lock, Unlock, Filter, Search, Eye, XCircle } from 'lucide-react';
import { FilterSection, LoadingState, EmptyState, useToast, LoadingSkeleton, Tooltip } from '../components/ui';
import api from '../services/api';

const GRNManagement = () => {
  const queryClient = useQueryClient();
  const { success, error: showError } = useToast();

  const [filters, setFilters] = useState({
    search: '',
    form_type: 'all',
    stock_type: 'all',
    status: 'all'
  });

  const [selectedGRN, setSelectedGRN] = useState(null);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [grnToCancel, setGrnToCancel] = useState(null);
  const [cancellationReason, setCancellationReason] = useState('');

  // Fetch GRNs with filters
  const { data: grnsData, isLoading } = useQuery({
    queryKey: ['grns', filters],
    queryFn: async () => {
      const params = {};
      if (filters.status !== 'all') params.status = filters.status;

      const response = await api.get('/qc/grn/', { params });
      return response.data;
    },
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  const grns = grnsData?.results || grnsData || [];

  // Apply client-side filters
  const filteredGRNs = grns.filter(grn => {
    // Search filter
    if (filters.search) {
      const search = filters.search.toLowerCase();
      const matchesSearch =
        grn.grn_number.toLowerCase().includes(search) ||
        grn.po_number?.toLowerCase().includes(search) ||
        grn.truck_number?.toLowerCase().includes(search) ||
        grn.sap_material_document?.toLowerCase().includes(search);
      if (!matchesSearch) return false;
    }

    // Form type filter
    if (filters.form_type !== 'all' && grn.form_type !== filters.form_type) {
      return false;
    }

    // Stock type filter
    if (filters.stock_type !== 'all' && grn.stock_type !== filters.stock_type) {
      return false;
    }

    return true;
  });

  const handleFilterChange = (name, value) => {
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      form_type: 'all',
      stock_type: 'all',
      status: 'all'
    });
  };

  const getFormTypeBadge = (formType, stockType) => {
    if (formType === '103') {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
          <Lock className="w-3 h-3 mr-1" />
          Form 103 - Blocked
        </span>
      );
    } else if (formType === '105') {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <Unlock className="w-3 h-3 mr-1" />
          Form 105 - Released
        </span>
      );
    }
    return null;
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      'DRAFT': 'bg-gray-100 text-gray-800',
      'PENDING_SAP': 'bg-yellow-100 text-yellow-800',
      'POSTED': 'bg-green-100 text-green-800',
      'FAILED': 'bg-red-100 text-red-800',
      'CANCELLED': 'bg-red-100 text-red-800'
    };
    return (
      <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColors[status] || 'bg-gray-100 text-gray-800'}`}>
        {status}
      </span>
    );
  };

  // Cancellation mutations
  const cancelForm103Mutation = useMutation({
    mutationFn: async ({ grnId, reason }) => {
      const response = await api.post(`/qc/grn/${grnId}/cancel/`, {
        cancellation_reason: reason
      });
      return response.data;
    },
    onSuccess: (data) => {
      success(`Form 104 cancellation created: ${data.form_104_grn}`);
      queryClient.invalidateQueries(['grns']);
      setCancelModalOpen(false);
      setGrnToCancel(null);
      setCancellationReason('');
    },
    onError: (err) => {
      showError(err.response?.data?.error || 'Failed to cancel GRN');
    }
  });

  const cancelForm105Mutation = useMutation({
    mutationFn: async ({ grnId, reason }) => {
      const response = await api.post(`/qc/grn/${grnId}/cancel-release/`, {
        cancellation_reason: reason
      });
      return response.data;
    },
    onSuccess: (data) => {
      success(`Form 106 cancellation created: ${data.form_106_grn}`);
      queryClient.invalidateQueries(['grns']);
      setCancelModalOpen(false);
      setGrnToCancel(null);
      setCancellationReason('');
    },
    onError: (err) => {
      showError(err.response?.data?.error || 'Failed to cancel GRN release');
    }
  });

  const handleCancelClick = (grn) => {
    setGrnToCancel(grn);
    setCancelModalOpen(true);
  };

  const handleCancelSubmit = () => {
    if (!cancellationReason.trim()) {
      showError('Please provide a cancellation reason');
      return;
    }

    if (grnToCancel.form_type === '103') {
      cancelForm103Mutation.mutate({
        grnId: grnToCancel.id,
        reason: cancellationReason
      });
    } else if (grnToCancel.form_type === '105') {
      cancelForm105Mutation.mutate({
        grnId: grnToCancel.id,
        reason: cancellationReason
      });
    }
  };

  const stats = [
    {
      label: 'Total GRNs',
      value: filteredGRNs.length,
      color: 'bg-blue-500'
    },
    {
      label: 'Form 103 (Blocked)',
      value: filteredGRNs.filter(g => g.form_type === '103').length,
      color: 'bg-orange-500'
    },
    {
      label: 'Form 105 (Released)',
      value: filteredGRNs.filter(g => g.form_type === '105').length,
      color: 'bg-green-500'
    },
    {
      label: 'Posted to SAP',
      value: filteredGRNs.filter(g => g.status === 'POSTED').length,
      color: 'bg-purple-500'
    }
  ];

  const filterOptions = [
    {
      name: 'form_type',
      label: 'Form Type',
      tooltip: 'Form 103: Blocks material (GR to Q-Stock) | Form 105: Releases material from Q-Stock',
      value: filters.form_type,
      options: [
        { value: '103', label: 'Form 103 - Blocked Stock' },
        { value: '105', label: 'Form 105 - Released Stock' }
      ]
    },
    {
      name: 'stock_type',
      label: 'Stock Type',
      tooltip: 'BLOCKED: Quality hold | UNRESTRICTED: Available for use | RESTRICTED: Limited use',
      value: filters.stock_type,
      options: [
        { value: 'BLOCKED', label: 'Blocked / Q-Stock' },
        { value: 'UNRESTRICTED', label: 'Unrestricted' },
        { value: 'RESTRICTED', label: 'Restricted' }
      ]
    },
    {
      name: 'status',
      label: 'Status',
      tooltip: 'DRAFT: Not yet posted | PENDING_SAP: Awaiting SAP response | POSTED: Successfully posted to SAP | FAILED: SAP posting failed',
      value: filters.status,
      options: [
        { value: 'DRAFT', label: 'Draft' },
        { value: 'PENDING_SAP', label: 'Pending SAP' },
        { value: 'POSTED', label: 'Posted' },
        { value: 'FAILED', label: 'Failed' }
      ]
    }
  ];

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center gap-2">
          <h1 className="text-3xl font-bold text-gray-900">GRN Management</h1>
          <Tooltip content="Form 103: Material blocked for quality inspection (Q-Stock) | Form 105: Material released from Q-Stock to unrestricted use | Form 104/106: Cancellation forms" position="right" />
        </div>
        <p className="text-gray-600 mt-2">Manage Goods Receipt Notes (Form 103 & 105)</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <div key={index} className="card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">{stat.label}</p>
                <p className="text-3xl font-bold mt-1">{stat.value}</p>
              </div>
              <div className={`${stat.color} text-white p-3 rounded-lg`}>
                <FileText className="w-6 h-6" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <FilterSection
        filters={filterOptions}
        onFilterChange={handleFilterChange}
        onClearFilters={handleClearFilters}
        searchValue={filters.search}
        searchPlaceholder="Search by GRN, PO, Truck, SAP Doc..."
      />

      {/* GRN List */}
      <div className="card">
        <h2 className="text-xl font-semibold mb-4">GRN List</h2>

        {isLoading ? (
          <LoadingSkeleton type="table" />
        ) : filteredGRNs.length === 0 ? (
          <EmptyState
            icon={FileText}
            title="No GRNs found"
            message={filters.search || filters.form_type !== 'all'
              ? "Try adjusting your filters"
              : "No GRNs created yet"}
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-3">GRN Number</th>
                  <th className="text-left p-3">Form Type</th>
                  <th className="text-left p-3">Stock Type</th>
                  <th className="text-left p-3">PO / Truck</th>
                  <th className="text-left p-3">Quantity</th>
                  <th className="text-left p-3">Value</th>
                  <th className="text-left p-3">Movement Type</th>
                  <th className="text-left p-3">Status</th>
                  <th className="text-left p-3">SAP Doc</th>
                  <th className="text-left p-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredGRNs.map((grn) => (
                  <tr key={grn.id} className="border-b hover:bg-gray-50 animate-fade-in">
                    <td className="p-3">
                      <div className="font-semibold text-nexgensis-primary">
                        {grn.grn_number}
                      </div>
                      <div className="text-xs text-gray-500">
                        {new Date(grn.grn_date).toLocaleString()}
                      </div>
                    </td>
                    <td className="p-3">
                      {getFormTypeBadge(grn.form_type, grn.stock_type)}
                    </td>
                    <td className="p-3">
                      <span className="text-sm text-gray-700">
                        {grn.stock_type_display}
                      </span>
                    </td>
                    <td className="p-3">
                      {grn.po_number && (
                        <div className="text-sm">
                          <div>PO: {grn.po_number}</div>
                          {grn.truck_number && (
                            <div className="text-gray-500">Truck: {grn.truck_number}</div>
                          )}
                        </div>
                      )}
                      {!grn.po_number && grn.supplier_name && (
                        <div className="text-sm text-gray-600">{grn.supplier_name}</div>
                      )}
                      {grn.source_grn_number && (
                        <div className="text-xs text-blue-600 mt-1">
                          From: {grn.source_grn_number}
                        </div>
                      )}
                      {grn.qc_inspection_number && (
                        <div className="text-xs text-green-600">
                          QC: {grn.qc_inspection_number}
                        </div>
                      )}
                    </td>
                    <td className="p-3">
                      <div className="text-sm font-medium">
                        {parseFloat(grn.total_quantity).toFixed(2)}
                      </div>
                    </td>
                    <td className="p-3">
                      <div className="text-sm font-medium">
                        ₹{parseFloat(grn.total_value).toLocaleString()}
                      </div>
                    </td>
                    <td className="p-3">
                      <code className="text-xs bg-gray-100 px-2 py-1 rounded">
                        {grn.sap_movement_type}
                      </code>
                    </td>
                    <td className="p-3">
                      {getStatusBadge(grn.status)}
                    </td>
                    <td className="p-3">
                      {grn.sap_material_document ? (
                        <div className="text-sm">
                          <div className="font-mono">{grn.sap_material_document}</div>
                          <div className="text-xs text-gray-500">{grn.sap_document_year}</div>
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">Not posted</span>
                      )}
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setSelectedGRN(grn)}
                          className="btn-secondary text-sm inline-flex items-center"
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View
                        </button>

                        {/* Show Cancel button only for posted Form 103/105 that are not already cancelled */}
                        {grn.status === 'POSTED' && !grn.is_cancelled && (grn.form_type === '103' || grn.form_type === '105') && (
                          <button
                            onClick={() => handleCancelClick(grn)}
                            className="text-red-600 hover:text-red-800 transition-colors p-1.5 rounded hover:bg-red-50"
                            title={`Cancel ${grn.form_type === '103' ? 'Form 103' : 'Form 105'}`}
                          >
                            <XCircle className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* GRN Detail Modal */}
      {selectedGRN && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto animate-scale-in">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold">{selectedGRN.grn_number}</h2>
                <p className="text-gray-600 mt-1">{selectedGRN.form_type_display}</p>
              </div>
              <div className="space-y-2">
                {getFormTypeBadge(selectedGRN.form_type, selectedGRN.stock_type)}
                {getStatusBadge(selectedGRN.status)}
              </div>
            </div>

            {/* Workflow Visualization for Form 105 */}
            {selectedGRN.form_type === '105' && (selectedGRN.source_grn_details || selectedGRN.qc_inspection_details) && (
              <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h3 className="text-sm font-semibold text-blue-900 mb-3">Stock Release Workflow</h3>
                <div className="flex items-center space-x-4">
                  {/* Form 103 */}
                  {selectedGRN.source_grn_details && (
                    <>
                      <div className="flex-1 text-center">
                        <div className="bg-orange-100 text-orange-800 px-3 py-2 rounded-lg border-2 border-orange-300">
                          <Lock className="w-5 h-5 mx-auto mb-1" />
                          <div className="text-xs font-semibold">Form 103</div>
                          <div className="text-xs">{selectedGRN.source_grn_details.grn_number}</div>
                          <div className="text-xs text-orange-600 mt-1">Blocked Stock</div>
                          <div className="text-xs font-bold">{selectedGRN.source_grn_details.total_quantity}</div>
                        </div>
                      </div>
                      <div className="text-gray-400">→</div>
                    </>
                  )}

                  {/* QC Inspection */}
                  {selectedGRN.qc_inspection_details && (
                    <>
                      <div className="flex-1 text-center">
                        <div className="bg-blue-100 text-blue-800 px-3 py-2 rounded-lg border-2 border-blue-300">
                          <CheckCircle className="w-5 h-5 mx-auto mb-1" />
                          <div className="text-xs font-semibold">QC Inspection</div>
                          <div className="text-xs">{selectedGRN.qc_inspection_details.inspection_number}</div>
                          <div className={`text-xs font-bold ${
                            selectedGRN.qc_inspection_details.result === 'PASS' ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {selectedGRN.qc_inspection_details.result}
                          </div>
                          <div className="text-xs mt-1">
                            ✓ {selectedGRN.qc_inspection_details.accepted_quantity} |
                            ✗ {selectedGRN.qc_inspection_details.rejected_quantity}
                          </div>
                        </div>
                      </div>
                      <div className="text-gray-400">→</div>
                    </>
                  )}

                  {/* Form 105 */}
                  <div className="flex-1 text-center">
                    <div className="bg-green-100 text-green-800 px-3 py-2 rounded-lg border-2 border-green-300">
                      <Unlock className="w-5 h-5 mx-auto mb-1" />
                      <div className="text-xs font-semibold">Form 105</div>
                      <div className="text-xs">{selectedGRN.grn_number}</div>
                      <div className="text-xs text-green-600 mt-1">Unrestricted</div>
                      <div className="text-xs font-bold">{selectedGRN.total_quantity}</div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* GRN Details */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div>
                <label className="text-sm font-medium text-gray-700">GRN Date</label>
                <p className="text-gray-900">{new Date(selectedGRN.grn_date).toLocaleString()}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Stock Type</label>
                <p className="text-gray-900">{selectedGRN.stock_type_display}</p>
              </div>
              {selectedGRN.po_number && (
                <div>
                  <label className="text-sm font-medium text-gray-700">PO Number</label>
                  <p className="text-gray-900">{selectedGRN.po_number}</p>
                </div>
              )}
              {selectedGRN.truck_number && (
                <div>
                  <label className="text-sm font-medium text-gray-700">Truck Number</label>
                  <p className="text-gray-900">{selectedGRN.truck_number}</p>
                </div>
              )}
              <div>
                <label className="text-sm font-medium text-gray-700">Total Quantity</label>
                <p className="text-gray-900 font-semibold">{parseFloat(selectedGRN.total_quantity).toFixed(3)}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Total Value</label>
                <p className="text-gray-900 font-semibold">₹{parseFloat(selectedGRN.total_value).toLocaleString()}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">SAP Plant</label>
                <p className="text-gray-900">{selectedGRN.sap_plant}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Movement Type</label>
                <p className="text-gray-900 font-mono">{selectedGRN.sap_movement_type}</p>
              </div>
              {selectedGRN.sap_material_document && (
                <>
                  <div>
                    <label className="text-sm font-medium text-gray-700">SAP Material Document</label>
                    <p className="text-gray-900 font-mono">{selectedGRN.sap_material_document}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">SAP Document Year</label>
                    <p className="text-gray-900">{selectedGRN.sap_document_year}</p>
                  </div>
                </>
              )}
            </div>

            {/* Line Items */}
            {selectedGRN.line_items && selectedGRN.line_items.length > 0 && (
              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-3">Line Items</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-gray-50">
                        <th className="text-left p-2">Line</th>
                        <th className="text-left p-2">Material</th>
                        <th className="text-left p-2">Quantity</th>
                        <th className="text-left p-2">UoM</th>
                        <th className="text-left p-2">Unit Price</th>
                        <th className="text-left p-2">Value</th>
                        <th className="text-left p-2">Storage Loc</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedGRN.line_items.map((line) => (
                        <tr key={line.id} className="border-b">
                          <td className="p-2">{line.line_number}</td>
                          <td className="p-2">
                            <div className="font-medium">{line.material_code}</div>
                            <div className="text-xs text-gray-500">{line.material_description}</div>
                          </td>
                          <td className="p-2 font-medium">{parseFloat(line.quantity).toFixed(3)}</td>
                          <td className="p-2">{line.unit_of_measure}</td>
                          <td className="p-2">₹{parseFloat(line.unit_price).toFixed(2)}</td>
                          <td className="p-2 font-semibold">₹{parseFloat(line.line_value).toLocaleString()}</td>
                          <td className="p-2">{line.storage_location}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="flex justify-end">
              <button
                onClick={() => setSelectedGRN(null)}
                className="btn-secondary"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancellation Modal */}
      {cancelModalOpen && grnToCancel && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <XCircle className="w-6 h-6 text-red-600 mr-2" />
                <h3 className="text-lg font-semibold text-gray-900">
                  Cancel {grnToCancel.form_type === '103' ? 'Form 103' : 'Form 105'} GRN
                </h3>
              </div>

              <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
                <p className="text-sm text-yellow-800">
                  <strong>Warning:</strong> This will create a {grnToCancel.form_type === '103' ? 'Form 104' : 'Form 106'} reversal document.
                  {grnToCancel.form_type === '103' && ' This GRN has posted to SAP and will be reversed.'}
                </p>
              </div>

              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-2">
                  <strong>GRN Number:</strong> {grnToCancel.grn_number}
                </p>
                <p className="text-sm text-gray-600 mb-2">
                  <strong>Stock Type:</strong> {grnToCancel.stock_type_display}
                </p>
                <p className="text-sm text-gray-600">
                  <strong>Quantity:</strong> {grnToCancel.total_quantity}
                </p>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Cancellation Reason <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={cancellationReason}
                  onChange={(e) => setCancellationReason(e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  placeholder="Enter reason for cancellation..."
                  required
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setCancelModalOpen(false);
                    setGrnToCancel(null);
                    setCancellationReason('');
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  disabled={cancelForm103Mutation.isLoading || cancelForm105Mutation.isLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleCancelSubmit}
                  disabled={!cancellationReason.trim() || cancelForm103Mutation.isLoading || cancelForm105Mutation.isLoading}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {(cancelForm103Mutation.isLoading || cancelForm105Mutation.isLoading) ? 'Cancelling...' : 'Confirm Cancellation'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GRNManagement;
